/**
 * LifecycleActions Component
 *
 * Container component that renders lifecycle action buttons based on
 * the current state of a record. Filters actions to only show those
 * valid for the record's current lifecycle state.
 */

import { Space } from 'antd'
import { LifecycleActionButton } from './LifecycleActionButton'
import type { LifecycleMetadata, FieldMetadata } from '@/services/metadataService'

/**
 * Props for LifecycleActions container
 */
export interface LifecycleActionsProps {
  /** Lifecycle metadata from backend */
  lifecycle: LifecycleMetadata
  /** The record to show actions for */
  record: Record<string, unknown>
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** Entity label for messages (e.g., 'Offering') */
  entityLabel: string
  /** Field metadata for diff display in splicing preview (optional - passed from MetadataTable) */
  fieldMetadata?: Record<string, FieldMetadata>
  /** Key fields from comparison config (for diff display in splicing preview) */
  comparisonKeyFields?: string[]
  /** Field to use for timeline segment labels (from comparison.timeline_label_field), or null to disable labels */
  timelineLabelField?: string | null
  /** Whether to prefix "NEW" to the focus segment in timeline (from comparison.show_new_prefix) */
  showNewPrefix?: boolean
  /** Called after successful action */
  onSuccess?: () => void
  /** Called for actions that open an editor (e.g., modify) */
  onOpenEditor?: (record: Record<string, unknown>, actionKey: string) => void
}

/**
 * LifecycleActions Component
 *
 * Filters and renders lifecycle action buttons based on the record's current state.
 *
 * @example
 * ```tsx
 * <LifecycleActions
 *   lifecycle={metadata.lifecycle}
 *   record={offering}
 *   endpoint="/offerings"
 *   entityLabel="Offering"
 *   onSuccess={() => refetch()}
 *   onOpenEditor={(record, action) => openModifyDrawer(record)}
 * />
 * ```
 */
export function LifecycleActions({
  lifecycle,
  record,
  endpoint,
  entityLabel,
  fieldMetadata,
  comparisonKeyFields,
  timelineLabelField,
  showNewPrefix,
  onSuccess,
  onOpenEditor,
}: LifecycleActionsProps) {
  // Get current state from record using the stateField from metadata
  const currentState = record[lifecycle.stateField] as string | undefined

  // If record has no state, don't render any actions
  if (!currentState) {
    return null
  }

  // Filter actions to only those valid for current state
  const applicableActions = Object.entries(lifecycle.actions).filter(
    ([, action]) => action.fromStates.includes(currentState)
  )

  // No applicable actions for current state
  if (applicableActions.length === 0) {
    return null
  }

  return (
    <Space size={4} wrap={false}>
      {applicableActions.map(([actionKey, action]) => (
        <LifecycleActionButton
          key={actionKey}
          actionKey={actionKey}
          action={action}
          record={record}
          endpoint={endpoint}
          entityLabel={entityLabel}
          fieldMetadata={fieldMetadata}
          comparisonKeyFields={comparisonKeyFields}
          timelineLabelField={timelineLabelField}
          showNewPrefix={showNewPrefix}
          onSuccess={onSuccess}
          onOpenEditor={onOpenEditor}
        />
      ))}
    </Space>
  )
}
